/**
 * Pure helpers for the creator page (`/c/<username>`, `pages/CreatorPage.tsx`): the shape of what the server
 * loader hands over, and the few rules the page itself decides. No React, no I/O, so the server render and the
 * client's hydrating render, which both call these with the same SSR payload, cannot disagree.
 * Spec: stippl-knowledge `work/creator-page.md`.
 *
 * Everything the page links to arrives as a finished href from the loader (`loadCreatorPage` in
 * graphql/helpers/creatorPageLoader.ts), tracking included. The one link built here is the app button.
 */

import { withCustomProductPage } from "components/common/customProductPage";

export interface MapPoint {
	/** Percent of the map image's width and height. */
	x: number;
	y: number;
}

export interface CreatorMap {
	image: string;
	width: number;
	height: number;
	points: MapPoint[];
}

export interface CreatorTripCard {
	id: string;
	title: string;
	flag: string;
	days: number;
	stops: number;
	cover: string | null;
	/** Tried in order when `cover` fails to load. */
	coverFallbacks?: string[];
	status: "past" | "now" | "upcoming";
	href: string;
	shareUrl: string;
	map: CreatorMap | null;
}

export interface CreatorNextTrip {
	mode: "now" | "next";
	title: string;
	flag: string;
	days: number;
	stops: number;
	startLabel: string;
	day: number | null;
	currentStop: string | null;
	makeItYoursHref: string;
	followHref: string;
	map: (CreatorMap & { currentIndex: number | null }) | null;
}

export interface CreatorListPin extends MapPoint {
	glyph: string;
	tint: string;
}

export interface CreatorListCard {
	id: string;
	title: string;
	placeCount: number;
	description: string;
	cover: string | null;
	/** Tried in order when `cover` fails to load. */
	coverFallbacks?: string[];
	href: string;
	map: { image: string; width: number; height: number; pins: CreatorListPin[] } | null;
}

/** What `loadCreatorPage` returns (graphql/helpers/creatorPageLoader.ts has the same shape: change both). */
export interface CreatorPageData {
	username: string;
	displayName: string;
	avatar: string | null;
	hometown: { name: string; flag: string } | null;
	published: boolean;
	accent: string;
	tagline: string;
	hello: string;
	intro: string;
	signature: string;
	facts: { emoji: string; text: string }[];
	heroPhotos: string[];
	polaroid: { image: string; caption: string } | null;
	socials: { type: string; label: string; url: string }[];
	followers: string | null;
	counts: { trips: number; countries: number };
	followHref: string;
	homeHref: string;
	creatorsHref: string;
	pageUrl: string;
	nextTrip: CreatorNextTrip | null;
	trips: CreatorTripCard[];
	lists: CreatorListCard[];
	credits: string[];
}

/**
 * The app button. OneLink on every device, in the universal-link form (`/cgUq/`, which is what lets an installed iOS
 * app open from it) and with the Apple Custom Product Page, exactly as `bioLinks.ts` builds its app links. `pid=creator`
 * and `c=<username>` credit the install to the creator; `af_sub1` says which button.
 */
export const creatorAppLink = (username: string, placement: string): string =>
	withCustomProductPage(`https://stippl.onelink.me/cgUq/?pid=creator&c=${encodeURIComponent(username)}&af_sub1=${encodeURIComponent(placement)}`);

/** "Ilse Jansen" -> "IJ": what the avatar shows when there is no photo. A word with no letter or digit in it ("&", an emoji) has no initial. */
export const initialsOf = (name: string): string =>
	name
		.split(/\s+/)
		.filter((word) => /[\p{L}\p{N}]/u.test(word))
		.slice(0, 2)
		.map((word) => Array.from(word)[0].toUpperCase())
		.join("");

/** The tagline with its last two words set apart: the page underlines them. A one- or two-word tagline is all underline. */
export const splitTagline = (tagline: string): { lead: string; accent: string } => {
	const words = tagline.trim().split(/\s+/).filter(Boolean);
	return { lead: words.slice(0, -2).join(" "), accent: words.slice(-2).join(" ") };
};

export const plural = (count: number, one: string, many: string = `${one}s`): string => (count === 1 ? one : many);

/** True for a point that sits where an earlier one does: a trip that ends where it began is drawn with one dot there and a line back to it. */
export const isRepeat = (points: MapPoint[], index: number): boolean => points.slice(0, index).some((p) => p.x === points[index].x && p.y === points[index].y);

/**
 * The curved line through a route's stops, in the pixel space of the map (`width` x `height`): each leg bows to one side
 * by `bends[i]` times its length, the sides alternating when two bends are given (the cards) and not when there is one
 * (the next-trip bar). Empty for fewer than two points.
 */
export const curvePath = (points: MapPoint[], width: number, height: number, bends: number[]): string => {
	if (points.length < 2) return "";
	const px = points.map((p) => [(p.x / 100) * width, (p.y / 100) * height]);
	let d = `M${px[0][0].toFixed(1)},${px[0][1].toFixed(1)}`;
	for (let i = 1; i < px.length; i++) {
		const [ax, ay] = px[i - 1];
		const [bx, by] = px[i];
		const k = bends[(i - 1) % bends.length];
		d += ` Q${((ax + bx) / 2 - (by - ay) * k).toFixed(1)},${((ay + by) / 2 + (bx - ax) * k).toFixed(1)} ${bx.toFixed(1)},${by.toFixed(1)}`;
	}
	return d;
};
